import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Select,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { BsFillSendFill, BsFillTrashFill } from 'react-icons/bs';
import {
  useChangeOrgMemberRoleMutation,
  useGetOrgMembersQuery,
  useGetOrgInvitesQuery,
  useGetOrgMeQuery,
  useDeleteOrgInviteMutation,
  usePatchOrganizationNameMutation,
  useResendOrgInviteMutation,
} from '../../Services/orgApi';
import TableFallback from '../../Components/Loader/TableFallback';
import InviteModal from './InviteModal';
import RemoveMemberModal from './RemoveMemberModal';

const roleOptions = ['viewer', 'editor', 'admin', 'owner'];

const formatMemberDisplayName = (memberEmail, currentUser) => {
  if (!currentUser || memberEmail !== currentUser.user) return null;
  const parts = [currentUser.name, currentUser.family_name].filter(Boolean);
  const displayName = parts.join(' ').trim();
  return displayName || null;
};

const OrgMembersPage = () => {
  const { account, user } = useSelector((s) => s.account);
  const myRole = useSelector((state) => state.account.memberRole || 'viewer');
  const roleIndex = roleOptions.indexOf(myRole);
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure();
  const {
    isOpen: isRemoveOpen,
    onOpen: onRemoveOpen,
    onClose: onRemoveClose,
  } = useDisclosure();
  const toast = useToast();
  const {
    data,
    isLoading,
    isFetching,
  } = useGetOrgMembersQuery({ accountId: account }, { skip: !account });

  const {
    data: invitesData,
    isLoading: invitesLoading,
    isFetching: invitesFetching,
  } = useGetOrgInvitesQuery({ accountId: account }, { skip: !account });

  const { data: orgMe } = useGetOrgMeQuery(undefined, { skip: !account });
  const [nameDraft, setNameDraft] = useState('');
  const [patchName, patchNameState] = usePatchOrganizationNameMutation();
  const [memberToRemove, setMemberToRemove] = useState(null);

  const savedName = useMemo(() => {
    const row = orgMe?.organizationAccounts?.find((o) => o.accountId === account);
    return row?.name ?? '';
  }, [account, orgMe?.organizationAccounts]);

  useEffect(() => {
    setNameDraft(savedName);
  }, [savedName]);

  const [changeRole, changeState] = useChangeOrgMemberRoleMutation();
  const [resendInvite, resendState] = useResendOrgInviteMutation();
  const [deleteInvite, deleteInviteState] = useDeleteOrgInviteMutation();

  const items = useMemo(() => data?.items || [], [data]);
  const invites = useMemo(() => invitesData?.items || [], [invitesData]);
  const nameUnchanged = nameDraft === savedName;
  const showPendingInvites = invitesLoading || invites.length > 0;

  const onChangeRole = async (memberEmail, nextRole) => {
    if (!account) return;
    const member = items.find((m) => m.email === memberEmail);
    if (member.role === 'owner') {
      if (myRole !== 'owner') {
        toast({
          title: "You can't change role for this user",
          status: 'error',
          variant: 'left-accent',
          isClosable: true,
          position: 'top',
          duration: 3000,
        });
        return;
      }
      const isThereOtherOwner = items.some((m) => m.email !== memberEmail && m.role === 'owner');
      if (!isThereOtherOwner) {
        toast({
          title: 'You neet to set another owner before changing this user role',
          status: 'error',
          variant: 'left-accent',
          isClosable: true,
          position: 'top',
          duration: 3000,
        });
        return;
      }
    }
    await changeRole({ accountId: account, email: memberEmail, role: nextRole }).unwrap();
    toast({
      title: 'Role changed',
      description: `Role of ${memberEmail} change to ${nextRole}`,
      status: 'success',
      variant: 'left-accent',
      isClosable: true,
      position: 'top',
      duration: 3000,
    });
  };

  const openRemoveModal = (memberEmail) => {
    setMemberToRemove(memberEmail);
    onRemoveOpen();
  };

  const closeRemoveModal = () => {
    setMemberToRemove(null);
    onRemoveClose();
  };

  const onResendInvite = async (token) => {
    if (!account) return;
    await resendInvite({ accountId: account, token }).unwrap();
    toast({
      title: 'Invite resent to user',
      status: 'success',
      variant: 'left-accent',
      isClosable: true,
      position: 'top',
      duration: 3000,
    });
  };

  const onDeleteInvite = async (token) => {
    if (!account) return;
    await deleteInvite({ accountId: account, token }).unwrap();
    toast({
      title: 'Invite deleted',
      status: 'success',
      variant: 'left-accent',
      isClosable: true,
      position: 'top',
      duration: 3000,
    });
  };

  const onSaveOrganizationName = async () => {
    if (!account) return;
    try {
      await patchName({ accountId: account, name: nameDraft }).unwrap();
      toast({
        title: 'Organization name updated',
        status: 'success',
        variant: 'left-accent',
        isClosable: true,
        position: 'top',
        duration: 3000,
      });
    } catch (e) {
      toast({
        title: 'Could not update organization name',
        description: e?.data?.message || e?.error || 'Unknown error',
        status: 'error',
        variant: 'left-accent',
        isClosable: true,
        position: 'top',
        duration: 5000,
      });
    }
  };

  return (
    <Flex direction="column">
      <Flex alignItems="center" mb="16px">
        <Text fontSize="lg" color={textColor} fontWeight="bold">
          Organization members
        </Text>
        <Spacer />
        <Flex direction="column" fontSize="sm" fontWeight="bold" textAlign="left">
          <Text>{orgMe?.organizationName || account}</Text>
          <Text fontSize="xs" fontWeight="normal" opacity={0.75}>
            ID:
            {' '}
            {account}
          </Text>
        </Flex>
        {(myRole === 'admin' || myRole === 'owner') && (
          <Button ml="16px" size="sm" onClick={onInviteOpen}>
            Invite member
          </Button>
        )}
      </Flex>

      {isInviteOpen && <InviteModal isOpen={isInviteOpen} onClose={onInviteClose} />}

      {(myRole === 'admin' || myRole === 'owner') && account && (
        <Card my="22px" overflowX={{ sm: 'scroll', xl: 'hidden' }} pb="0px">
          <CardHeader pb="0">
            <Text fontSize="md" color={textColor} fontWeight="bold">
              Organization display name
            </Text>
          </CardHeader>
          <CardBody>
            <FormControl maxW="360px">
              <FormLabel fontSize="sm">Display name</FormLabel>
              <Flex gap="8px" flexWrap="wrap" alignItems="flex-end">
                <Input
                  size="sm"
                  placeholder="Organization name"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
                <Button
                  size="sm"
                  onClick={onSaveOrganizationName}
                  isLoading={patchNameState.isLoading}
                  isDisabled={nameUnchanged || patchNameState.isLoading}
                >
                  Save name
                </Button>
              </Flex>
              <FormHelperText>Shown in the app and invite emails</FormHelperText>
            </FormControl>
          </CardBody>
        </Card>
      )}

      <Card my="22px" overflowX={{ sm: 'scroll', xl: 'hidden' }} pb="0px">
        <CardHeader pb="0">
          <Text fontSize="md" color={textColor} fontWeight="bold">
            Members
          </Text>
        </CardHeader>
        <CardBody>
          <Table color={textColor} size="sm">
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Name</Th>
                <Th borderColor={borderColor}>Email</Th>
                <Th borderColor={borderColor}>Role</Th>
                <Th borderColor={borderColor} />
              </Tr>
            </Thead>
            <Tbody>
              {(isLoading || isFetching) ? (
                <TableFallback cols={4} />
              ) : (
                <>
                  {items.map((m) => {
                    const isCurrentUser = m.email === user.user;
                    const displayName = formatMemberDisplayName(m.email, user);
                    return (
                      <Tr key={m.email}>
                        <Td borderColor={borderColor}>
                          <Flex alignItems="center" gap="8px">
                            <Text>{displayName || '—'}</Text>
                            {isCurrentUser && (
                              <Badge colorScheme="blue" fontSize="xs">You</Badge>
                            )}
                          </Flex>
                        </Td>
                        <Td borderColor={borderColor}>{m.email}</Td>
                        <Td borderColor={borderColor}>
                          <Select
                            size="sm"
                            value={m.role || 'viewer'}
                            onChange={(e) => onChangeRole(m.email, e.target.value)}
                            isDisabled={isCurrentUser || changeState.isLoading}
                            maxW="160px"
                          >
                            {roleOptions.map((r, i) => (
                              <option key={r} value={r} disabled={i > roleIndex}>{r}</option>
                            ))}
                          </Select>
                        </Td>
                        <Td borderColor={borderColor} textAlign="right">
                          {!isCurrentUser && (
                            <Tooltip label="Remove member">
                              <IconButton
                                size="sm"
                                variant="outline"
                                colorScheme="red"
                                aria-label="Remove member"
                                onClick={() => openRemoveModal(m.email)}
                                icon={<Icon as={BsFillTrashFill} />}
                              />
                            </Tooltip>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                  {items.length === 0 && (
                    <Tr>
                      <Td colSpan={4} opacity={0.7} borderColor={borderColor}>
                        No members found.
                      </Td>
                    </Tr>
                  )}
                </>
              )}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      {showPendingInvites && (
        <Card my="22px" overflowX={{ sm: 'scroll', xl: 'hidden' }} pb="0px">
          <CardHeader pb="0">
            <Text fontSize="md" color={textColor} fontWeight="bold">
              Pending invites
            </Text>
          </CardHeader>
          <CardBody>
            <Table color={textColor} size="sm">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>Email</Th>
                  <Th borderColor={borderColor}>Role</Th>
                  <Th borderColor={borderColor} />
                </Tr>
              </Thead>
              <Tbody>
                {(invitesLoading || invitesFetching) ? (
                  <TableFallback cols={3} />
                ) : (
                  invites.map((inv) => (
                    <Tr key={inv.token}>
                      <Td borderColor={borderColor}>{inv.email}</Td>
                      <Td borderColor={borderColor}>{inv.role}</Td>
                      <Td borderColor={borderColor} textAlign="right">
                        <Flex justifyContent="flex-end" gap="8px">
                          <Tooltip label="Resend">
                            <IconButton
                              size="sm"
                              variant="outline"
                              aria-label="Resend invite"
                              onClick={() => onResendInvite(inv.token)}
                              isLoading={resendState.isLoading}
                              icon={<Icon as={BsFillSendFill} />}
                            />
                          </Tooltip>
                          <Tooltip label="Delete">
                            <IconButton
                              size="sm"
                              variant="outline"
                              colorScheme="red"
                              aria-label="Delete invite"
                              onClick={() => onDeleteInvite(inv.token)}
                              isLoading={deleteInviteState.isLoading}
                              icon={<Icon as={BsFillTrashFill} />}
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      <RemoveMemberModal
        isOpen={isRemoveOpen}
        onClose={closeRemoveModal}
        memberEmail={memberToRemove}
        accountId={account}
      />
    </Flex>
  );
};

export default OrgMembersPage;
